import type { DebateMap } from "@/hooks/useFieldDebate";
import type { PaperIdScopeKind } from "@/stores/filter-store";

export function encodeScopeForUrl(
  kind: PaperIdScopeKind,
  paperIds: string[] | undefined,
  label: string | undefined,
  debateMap: DebateMap | undefined,
): string | undefined {
  if (!debateMap) return undefined;
  switch (kind) {
    case "field-debate-camp": {
      // Find the camp whose paper_ids match the scope (first hit wins)
      const camp = debateMap.camps.find(
        (c) =>
          paperIds &&
          c.paper_ids.length === paperIds.length &&
          c.paper_ids.every((id, i) => id === paperIds[i]),
      );
      return camp ? `camp:${camp.id}` : undefined;
    }
    case "field-debate-bridges":
      return "bridges:all";
    case "field-debate-tension": {
      // Tension labels follow "{a_label} × {b_label}" — split and look up.
      if (!label) return undefined;
      const [aLabel, bLabel] = label.split(" × ");
      const a = debateMap.camps.find((c) => c.label === aLabel);
      const b = debateMap.camps.find((c) => c.label === bLabel);
      if (!a || !b) return undefined;
      return `tension:${a.id}__${b.id}`;
    }
  }
}

export function decodeScopeFromUrl(
  scope: string | undefined,
  debateMap: DebateMap | undefined,
): { kind: PaperIdScopeKind; label: string; paperIds: string[] } | null {
  if (!scope || !debateMap) return null;
  const colonIdx = scope.indexOf(":");
  if (colonIdx === -1) return null;
  const prefix = scope.slice(0, colonIdx);
  const payload = scope.slice(colonIdx + 1);
  if (prefix === "camp") {
    const camp = debateMap.camps.find((c) => c.id === payload);
    if (!camp) return null;
    return {
      kind: "field-debate-camp",
      label: camp.label,
      paperIds: camp.paper_ids,
    };
  }
  if (prefix === "bridges") {
    return {
      kind: "field-debate-bridges",
      label: "Bridge papers",
      paperIds: debateMap.bridge_papers.map((bp) => bp.paper_id),
    };
  }
  if (prefix === "tension") {
    const [aId, bId] = payload.split("__");
    const a = debateMap.camps.find((c) => c.id === aId);
    const b = debateMap.camps.find((c) => c.id === bId);
    if (!a || !b) return null;
    const ids = Array.from(new Set([...a.paper_ids, ...b.paper_ids]));
    return {
      kind: "field-debate-tension",
      label: `${a.label} × ${b.label}`,
      paperIds: ids,
    };
  }
  return null;
}
